import { NoteTemplateFormat } from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';
import type {
  Document,
  DocumentSectionOverride,
} from '@/shared/api/transcriptor';

import {
  createTempId,
  getDocumentTypeRules,
  isBlockNoteTemplate,
  normalizeDocumentType,
  parseDocumentType,
  type DocumentSectionSpec,
  type DocumentTemplate,
  type DocumentTemplateSummary,
} from '@/entities/templates';

import { TRANSCRIPT_QUALITY_SECTION_CODES } from '../config';

import { createNoteBlock, createNoteLine } from './blockTemplate';
import { createSlotId } from './templateDraft';

// Summaries, evaluations and letters: a list of fields, each a title and the
// instruction the model answers under it. A template without a type is a
// protocol, as its version request reads it.
export const isFieldListTemplate = (
  template: Pick<DocumentTemplateSummary, 'type' | 'format'>,
) =>
  !isBlockNoteTemplate(template) &&
  !getDocumentTypeRules(normalizeDocumentType(template.type)).allowOptions;

// An encounter's summary or evaluation, pinned to a version of its template.
export const isFieldListDocument = (document: Document) =>
  (document.type === 'summary' || document.type === 'evaluation') &&
  !!document.id &&
  !!document.template_id;

// Codes are ASCII snake_case like the templates' own. A title without Latin
// or Cyrillic letters gives a numbered generic code.
const createFieldCode = (title: string, taken: string[]) =>
  createSlotId(title, taken) || createSlotId('field', taken);

// A field added to the list: required, with no options or lines; its code
// is given by withFieldCodes.
export const createListField = (title: string): DocumentSectionSpec => ({
  ID: createTempId(),
  Code: '',
  Title: title,
  EditorType: '',
  Type: '',
  ProcessingType: '',
  Options: [],
  TemplateType: '',
  Required: true,
  Lines: [],
});

// Generated documents keep a field's text under its code, so a field of the
// saved version keeps its code under any title. A field the version does not
// hold takes its code from its title until the version is saved, never a
// code the version or another field holds.
export const withFieldCodes = (
  template: DocumentTemplate,
  source: DocumentTemplate,
): DocumentTemplate => {
  const saved = source.sections.map((section) => section.Code);
  const sections = template.sections.reduce<DocumentSectionSpec[]>(
    (done, section, index) => {
      if (saved.includes(section.Code)) {
        return [...done, section];
      }
      const taken = [
        ...saved,
        ...done.map((field) => field.Code),
        ...template.sections
          .slice(index + 1)
          .filter((field) => saved.includes(field.Code))
          .map((field) => field.Code),
      ];
      const Code = createFieldCode(section.Title, taken);
      return [...done, Code === section.Code ? section : { ...section, Code }];
    },
    [],
  );
  return sections.every(
    (section, index) => section === template.sections[index],
  )
    ? template
    : { ...template, sections };
};

// An evaluation scores the transcript it was made from before its stages:
// that section is shown first and without a number. Only the display
// changes; the template and the evaluation keep it where it is stored.
export const isTranscriptQualitySection = (
  documentType: string | null | undefined,
  code: string | undefined,
) =>
  parseDocumentType(documentType) === 'EVALUATION' &&
  !!code &&
  TRANSCRIPT_QUALITY_SECTION_CODES.includes(code);

export const showTranscriptQualityFirst = <T>(
  documentType: string | null | undefined,
  sections: T[],
  readCode: (section: T) => string | undefined,
): T[] => {
  const first = sections.filter((section) =>
    isTranscriptQualitySection(documentType, readCode(section)),
  );
  return first.length
    ? [...first, ...sections.filter((section) => !first.includes(section))]
    : sections;
};

// The fields as they were shown, with the transcript quality first, put back
// in the order they are stored in: that section goes before the first field
// it was stored before, or last.
export const restoreStoredFieldOrder = (
  shown: DocumentTemplate,
  stored: DocumentTemplate,
): DocumentTemplate => {
  const isFirst = (section: DocumentSectionSpec) =>
    isTranscriptQualitySection(shown.type, section.Code);
  const sections = shown.sections.filter(isFirst).reduce<DocumentSectionSpec[]>(
    (placed, section) => {
      const storedAt = stored.sections.findIndex(
        (item) => item.ID === section.ID,
      );
      const before =
        storedAt < 0
          ? undefined
          : stored.sections
              .slice(storedAt + 1)
              .find((item) => placed.some((field) => field.ID === item.ID));
      const at = before
        ? placed.findIndex((field) => field.ID === before.ID)
        : placed.length;
      return [...placed.slice(0, at), section, ...placed.slice(at)];
    },
    shown.sections.filter((section) => !isFirst(section)),
  );
  return sections.every((section, index) => section === shown.sections[index])
    ? shown
    : { ...shown, sections };
};

// The editor keys every field by its section id, which a rename keeps. A
// field saved without an id, or with one an earlier field has, is keyed by a
// temporary id meanwhile, as an added field is: scribe gives a section saved
// with a temporary id an id of its own.
export const withFieldListKeys = (
  template: DocumentTemplate,
): DocumentTemplate => {
  const sections = template.sections.map((section, index, all) =>
    section.ID && all.findIndex((other) => other.ID === section.ID) === index
      ? section
      : { ...section, ID: `tmp_field_${index}_${section.Code}` },
  );
  return sections.some((section, index) => section !== template.sections[index])
    ? { ...template, sections }
    : template;
};

// The sections of a field list as a recording takes them in place of its
// version's: every section's code, title and instruction, in order.
export const toSectionOverrides = (
  template: DocumentTemplate,
): DocumentSectionOverride[] =>
  template.sections.map((section) => ({
    code: section.Code,
    title: section.Title.trim(),
    instructions: section.Instructions?.trim() || undefined,
  }));

// Whether the titles or instructions set before a recording differ from the
// version's.
export const holdsSectionEdits = (
  edited: DocumentTemplate,
  source: DocumentTemplate,
) =>
  JSON.stringify(toSectionOverrides(edited)) !==
  JSON.stringify(toSectionOverrides(source));

// A version as a recording reads it: the titles and instructions set before
// it laid over the version's sections, which they name in order. Overrides
// that no longer name them leave the version as it is.
export const applySectionOverrides = (
  template: DocumentTemplate,
  overrides: DocumentSectionOverride[] | undefined,
): DocumentTemplate =>
  overrides?.length &&
  overrides.length === template.sections.length &&
  overrides.every(
    (override, index) => override.code === template.sections[index].Code,
  )
    ? {
        ...template,
        sections: template.sections.map((section, index) => ({
          ...section,
          Title: overrides[index].title,
          Instructions: overrides[index].instructions,
        })),
      }
    : template;

export const prepareBlockSummary = (
  template: DocumentTemplate,
): DocumentTemplate => {
  if (
    template.type !== 'SUMMARY' ||
    !template.transcriptor_v2 ||
    isBlockNoteTemplate(template)
  ) {
    return template;
  }
  return {
    ...template,
    format: NoteTemplateFormat.NoteTemplateFormatBlocks,
    sections: withFieldListKeys(template).sections.map((section, index) => ({
      ...section,
      Lines: section.Lines.length
        ? section.Lines
        : [
            {
              ...createNoteLine({
                ...createNoteBlock('freeText'),
                ID: `tmp_summary_block_${index}`,
                SlotID: section.Code,
              }),
              ID: `tmp_summary_line_${index}`,
            },
          ],
    })),
  };
};
